/**
 * Client-chart actions that start Presenting Problems and IRP drafts,
 * pre-filled from the client's signed referral / presenting problems, or an
 * IRP review copied forward from the last signed IRP.
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import {
  CalendarRange,
  FilePlus2,
  Gauge,
  NotebookPen,
  RotateCcw,
  Search,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/hooks/use-auth';
import { getAccessToken } from '@/api/client';
import { rolesFromAccessToken } from '@/lib/prp-home';
import { useDebounce } from '@/hooks/use-debounce';
import { useSearchPatients } from '@/hooks/use-patients';
import { getPatient } from '@/api/patient.api';
import type { Patient } from '@/types';
import { mutationMeta } from '@/lib/query-client';
import {
  createPrpDocument,
  getPrpCaseload,
  getPrpDocument,
  listPrpDocuments,
  PRP_DOC_LABELS,
  type PrpDocType,
  type PrpDocument,
} from '@/api/prp.api';
import { parsePresentingProblems, presentingProblemsFromReferral } from './forms/pp-rules';
import { emptyDla20, parseDla20 } from './forms/dla20-rules';
import { emptyMonthlySummary } from './forms/monthly-rules';
import { irpFromSources } from './forms/irp-rules';
import { parseReferral } from './forms/referral-rules';
import { todayISO } from './format';
import { useStartContactNote } from './use-start-contact-note';
import { documentOrigin } from './navigation';

/** Latest signed document of a type, with content (lists omit content). */
async function latestSigned(patientId: string, docType: PrpDocType): Promise<PrpDocument | null> {
  const { data } = await listPrpDocuments({
    patient_id: patientId,
    doc_type: docType,
    status: 'signed',
    limit: 1,
  });
  return data[0] ? getPrpDocument(data[0].id) : null;
}

function useOpenDraft(patientId: string, docType: PrpDocType) {
  return useQuery({
    queryKey: ['prp', 'documents', { patient_id: patientId, doc_type: docType, open: true }],
    queryFn: async () => {
      const [drafts, pending] = await Promise.all([
        listPrpDocuments({ patient_id: patientId, doc_type: docType, status: 'draft', limit: 1 }),
        listPrpDocuments({
          patient_id: patientId,
          doc_type: docType,
          status: 'pending_signatures',
          limit: 1,
        }),
      ]);
      return drafts.data[0] ?? pending.data[0] ?? null;
    },
  });
}

function useCreate(patientId: string, docType: PrpDocType) {
  const navigate = useNavigate();
  const location = useLocation();
  return useMutation({
    mutationFn: (opts: { content?: Record<string, unknown>; copyFromId?: string }) =>
      createPrpDocument({
        patient_id: patientId,
        doc_type: docType,
        document_date: todayISO(),
        content: opts.content,
        copy_from_id: opts.copyFromId,
      }),
    onSuccess: (doc) =>
      navigate(`/prp/documents/${doc.id}`, {
        state: documentOrigin(location.pathname, location.search),
      }),
    meta: mutationMeta.created(PRP_DOC_LABELS[docType]),
  });
}

function OpenDraftLink({ doc }: { doc: PrpDocument }) {
  const location = useLocation();
  return (
    <Button asChild size="sm" variant="outline">
      <Link
        to={`/prp/documents/${doc.id}`}
        state={documentOrigin(location.pathname, location.search)}
      >
        Open {doc.status === 'draft' ? 'draft' : 'document awaiting signatures'}
      </Link>
    </Button>
  );
}

/**
 * PRP counselors and Directors author IRPs, DLA-20s, contact notes and monthly
 * summaries - the author signs them as the counselor, so others can't finish
 * them (the server refuses too).
 */
function useIsPrpStaff(): boolean {
  const { user } = useAuth();
  const roles = [user?.role, ...(user?.selected_roles ?? []), ...rolesFromAccessToken(getAccessToken())];
  return roles.some((r) => r === 'prp_rc' || r === 'prp_director');
}

export function NewPresentingProblemsButton({ patientId }: { patientId: string }) {
  const { can } = usePermissions();
  const open = useOpenDraft(patientId, 'presenting_problems');
  const create = useCreate(patientId, 'presenting_problems');
  if (!can('prp', 'create') || open.isLoading) return null;
  if (open.data) return <OpenDraftLink doc={open.data} />;

  const start = async () => {
    const referral = await latestSigned(patientId, 'referral');
    const content = presentingProblemsFromReferral(
      referral ? parseReferral(referral.content) : null,
      todayISO(),
    );
    create.mutate({ content: content as unknown as Record<string, unknown> });
  };
  return (
    <Button size="sm" onClick={start} disabled={create.isPending}>
      <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> New Presenting Problems
    </Button>
  );
}

export function NewIrpButton({ patientId }: { patientId: string }) {
  const prpStaff = useIsPrpStaff();
  const { can } = usePermissions();
  const open = useOpenDraft(patientId, 'irp');
  const lastIrp = useQuery({
    queryKey: [
      'prp',
      'documents',
      { patient_id: patientId, doc_type: 'irp', status: 'signed', limit: 1 },
    ],
    queryFn: () =>
      listPrpDocuments({ patient_id: patientId, doc_type: 'irp', status: 'signed', limit: 1 }),
  });
  const create = useCreate(patientId, 'irp');
  if (!can('prp', 'create') || !prpStaff || open.isLoading || lastIrp.isLoading) return null;
  if (open.data) return <OpenDraftLink doc={open.data} />;

  const previous = lastIrp.data?.data[0];
  if (previous) {
    return (
      <Button
        size="sm"
        onClick={() => create.mutate({ copyFromId: previous.id })}
        disabled={create.isPending}
      >
        <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Start IRP review
      </Button>
    );
  }

  const start = async () => {
    const [referral, pp, dla] = await Promise.all([
      latestSigned(patientId, 'referral'),
      latestSigned(patientId, 'presenting_problems'),
      latestSigned(patientId, 'dla20'),
    ]);
    const content = irpFromSources(
      referral ? parseReferral(referral.content) : null,
      pp ? parsePresentingProblems(pp.content) : null,
      dla ? { content: parseDla20(dla.content), date: dla.document_date } : null,
    );
    create.mutate({ content: content as unknown as Record<string, unknown> });
  };
  return (
    <Button size="sm" onClick={start} disabled={create.isPending}>
      <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> New IRP
    </Button>
  );
}

export function NewContactNoteButton({ patientId }: { patientId: string }) {
  const prpStaff = useIsPrpStaff();
  const { can } = usePermissions();
  const start = useStartContactNote();
  if (!can('prp', 'create') || !prpStaff) return null;
  return (
    <Button size="sm" onClick={() => start.mutate(patientId)} disabled={start.isPending}>
      <NotebookPen className="mr-1.5 h-3.5 w-3.5" /> New contact note
    </Button>
  );
}

/** Contact Notes page: pick a client from the caseload, then start the note. */
export function NewContactNoteDialog({ onClose }: { onClose: () => void }) {
  const start = useStartContactNote();
  const mine = useQuery({
    queryKey: ['prp', 'caseload', 'mine'],
    queryFn: () => getPrpCaseload(true),
  });
  const all = useQuery({ queryKey: ['prp', 'caseload', 'all'], queryFn: () => getPrpCaseload() });
  const [query, setQuery] = useState('');
  const [patient, setPatient] = useState<Patient | null>(null);
  const debounced = useDebounce(query.trim(), 300);
  const search = useSearchPatients(patient ? '' : debounced);
  const mineIds = new Set((mine.data ?? []).map((row) => row.patient_id));
  const allIds = new Set((all.data ?? []).map((row) => row.patient_id));
  const previewRows = (mine.data?.length ? mine.data : (all.data ?? [])).slice(0, 5);
  const preview = useQueries({
    queries: previewRows.map((row) => ({
      queryKey: ['patients', row.patient_id],
      queryFn: () => getPatient(row.patient_id),
      staleTime: 60_000,
    })),
  });
  const matches = (search.data ?? []).filter((result) => allIds.has(result.id));
  const signedIrp = useQuery({
    queryKey: [
      'prp',
      'documents',
      { patient_id: patient?.id, doc_type: 'irp', status: 'signed', limit: 1 },
    ],
    queryFn: () =>
      listPrpDocuments({ patient_id: patient!.id, doc_type: 'irp', status: 'signed', limit: 1 }),
    enabled: !!patient,
  });

  function chooseClient(client: Patient) {
    setPatient(client);
    setQuery(`${client.last_name}, ${client.first_name}`);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New contact note</DialogTitle>
          <DialogDescription>
            Choose an active PRP client. The new note will use their current signed IRP.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-5 space-y-2">
          <Label htmlFor="prp-new-note-client">Client</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="prp-new-note-client"
              className="pl-9"
              placeholder="Search active clients by name or MRN"
              value={query}
              autoComplete="off"
              onChange={(event) => {
                setQuery(event.target.value);
                setPatient(null);
              }}
            />
          </div>
          {!patient && (
            <div className="max-h-52 overflow-y-auto rounded-xl border border-border bg-background p-1">
              {query.trim().length >= 3 ? (
                all.isLoading || debounced !== query.trim() || search.isLoading ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Searching clients…</p>
                ) : all.isError ? (
                  <p className="px-3 py-2 text-sm text-destructive">
                    Could not load active clients.
                  </p>
                ) : search.isError ? (
                  <p className="px-3 py-2 text-sm text-destructive">Could not search clients.</p>
                ) : matches.length ? (
                  matches.map((result) => (
                    <ClientOption
                      key={result.id}
                      patient={result}
                      isMine={mineIds.has(result.id)}
                      onClick={() => chooseClient(result)}
                    />
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    No matching active PRP clients.
                  </p>
                )
              ) : mine.isLoading || all.isLoading || preview.some((item) => item.isLoading) ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Loading clients…</p>
              ) : all.isError ? (
                <p className="px-3 py-2 text-sm text-destructive">Could not load active clients.</p>
              ) : preview.length ? (
                preview.map(
                  (item) =>
                    item.data && (
                      <ClientOption
                        key={item.data.id}
                        patient={item.data}
                        isMine={mineIds.has(item.data.id)}
                        onClick={() => chooseClient(item.data!)}
                      />
                    ),
                )
              ) : (
                <p className="px-3 py-2 text-sm text-muted-foreground">
                  No active PRP clients. Search to find a client.
                </p>
              )}
            </div>
          )}
          {patient && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <UserRound className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">
                    {patient.last_name}, {patient.first_name}
                  </p>
                  <p className="text-xs text-muted-foreground">MRN {patient.mrn}</p>
                </div>
              </div>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setPatient(null);
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          )}
          {patient &&
            (signedIrp.isLoading ? (
              <p className="text-xs text-muted-foreground">Checking for a signed IRP…</p>
            ) : signedIrp.isError ? (
              <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Could not check the client's IRP. Try again in a moment.
              </p>
            ) : !signedIrp.data?.data.length ? (
              <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                A signed IRP is required before starting a contact note.
              </p>
            ) : (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                Signed IRP found. Ready to start the note.
              </p>
            ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => patient && start.mutate(patient.id)}
            disabled={!patient || !signedIrp.data?.data.length || start.isPending}
          >
            {start.isPending ? 'Starting…' : 'Start note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientOption({
  patient,
  isMine,
  onClick,
}: {
  patient: Patient;
  isMine: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onClick={onClick}
    >
      <span className="font-medium">
        {patient.last_name}, {patient.first_name}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {isMine ? 'My client · ' : ''}
        {patient.mrn}
      </span>
    </button>
  );
}

export function NewDla20Button({ patientId }: { patientId: string }) {
  const prpStaff = useIsPrpStaff();
  const { can } = usePermissions();
  const open = useOpenDraft(patientId, 'dla20');
  const create = useCreate(patientId, 'dla20');
  if (!can('prp', 'create') || !prpStaff || open.isLoading) return null;
  if (open.data) return <OpenDraftLink doc={open.data} />;

  const start = async () => {
    const { data } = await listPrpDocuments({
      patient_id: patientId,
      doc_type: 'dla20',
      status: 'signed',
      limit: 1,
    });
    create.mutate({ content: emptyDla20(data[0]?.id ?? '') as unknown as Record<string, unknown> });
  };
  return (
    <Button size="sm" onClick={start} disabled={create.isPending}>
      <Gauge className="mr-1.5 h-3.5 w-3.5" /> New DLA-20
    </Button>
  );
}

/** Month just finished (summaries are usually written after month end). */
function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function NewMonthlySummaryButton({ patientId }: { patientId: string }) {
  const prpStaff = useIsPrpStaff();
  const { can } = usePermissions();
  const open = useOpenDraft(patientId, 'monthly_summary');
  const create = useCreate(patientId, 'monthly_summary');
  if (!can('prp', 'create') || !prpStaff || open.isLoading) return null;
  if (open.data) return <OpenDraftLink doc={open.data} />;
  return (
    <Button
      size="sm"
      onClick={() =>
        create.mutate({
          content: emptyMonthlySummary(lastMonth()) as unknown as Record<string, unknown>,
        })
      }
      disabled={create.isPending}
    >
      <CalendarRange className="mr-1.5 h-3.5 w-3.5" /> New monthly summary
    </Button>
  );
}
